import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './profile.css';
import ThemeToggle from '@/components/theme/ThemeToggle';
import { useAuth } from '@/components/auth/AuthContext';
import LanguageSwitch from '@/components/language/LanguageSwitch';
import { useI18n } from '@/i18n';
import EAction from './action_button/EAction';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const WarehouseRequestPanel = React.lazy(() => import('./action_button/warehouse_request'));
const ProcurementRequestPanel = React.lazy(() => import('./action_button/procurement_request'));
const RequestsViewPanel = React.lazy(() => import('./action_button/requests_view'));
const ViewDeptRequestPanel = React.lazy(() => import('./action_button/view_dept_request/view_dept_request_card'));
const ViewPendingRequestsPanel = React.lazy(() => import('./action_button/view_pending_requests/view_pending_requests'));
const PurchaseStartPanel = React.lazy(() => import('./action_button/purchase_start/purchase_start'));
const WarehouseStartPanel = React.lazy(() => import('./action_button/warehouse_start/warehouse_start'));
const WarehouseManagementPanel = React.lazy(() => import('./action_button/warehouse_start/warehouse_management/warehouse_items_card'));
const ViewCategoryRequestsModerator = React.lazy(() => import('./action_button/view_category_requests_moderator'));
const AIChatbotPanel = React.lazy(() => import('./action_button/ai_chatbot/ai_chatbot'));
const ViewDatabasePanel = React.lazy(() => import('./action_button/1_view_database/view_database'));

const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/,'');

type PanelView = 'none' | 'profile' | 'edit-menu' | 'edit-name' | 'edit-surname' | 'edit-phone' | 'edit-picture';
type ActionKind = 'warehouse' | 'procurement' | 'view' | 'view_dept' | 'view_pending' | 'purchase_start' | 'warehouse_start' | 'warehouse_management' | 'moderator_pending' | 'view_database' | 'ai_chatbot';

const actionToRoute: Record<ActionKind, string> = {
  warehouse: 'warehouse-request',
  procurement: 'procurement-request',
  view: 'my-requests',
  view_dept: 'dept-requests',
  view_pending: 'pending-requests',
  purchase_start: 'purchase-start',
  warehouse_start: 'warehouse-pending',
  warehouse_management: 'warehouse-management',
  moderator_pending: 'moderator-pending',
  view_database: 'view-database',
  ai_chatbot: 'ai-chatbot',
};

const routeToAction: Record<string, ActionKind> = {
  'warehouse-request': 'warehouse',
  'procurement-request': 'procurement',
  'my-requests': 'view',
  'dept-requests': 'view_dept',
  'pending-requests': 'view_pending',
  'purchase-start': 'purchase_start',
  'warehouse-pending': 'warehouse_start',
  'warehouse-management': 'warehouse_management',
  'moderator-pending': 'moderator_pending',
  'view-database': 'view_database',
  'ai-chatbot': 'ai_chatbot',
};

let avatarFetchPromise: Promise<string | null> | null = null;
let avatarFetchForUserId: number | null = null;
let cachedAvatarForUserId: number | null = null;
let cachedAvatarUrl: string | null = null;

const updateCachedAvatar = (userId: number | null, nextUrl: string | null) => {
  if (cachedAvatarUrl && cachedAvatarUrl !== nextUrl) {
    URL.revokeObjectURL(cachedAvatarUrl);
  }
  cachedAvatarUrl = nextUrl;
  cachedAvatarForUserId = userId;
};

const ProfilesPage: React.FC = () => {
  const navigate = useNavigate();
  const { panel: panelParam } = useParams<{ panel?: string }>();
  const { user, refresh, logout } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState(user);
  const [panel, setPanel] = useState<PanelView>('none');
  const [selectedAction, setSelectedAction] = useState<ActionKind>('view');
  const [panelMounted, setPanelMounted] = useState(false);
  const [profileBtnMounted, setProfileBtnMounted] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    setMe(user);
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    const currentUserId = user?.id ?? null;

    const loadAvatar = async () => {
      try {
        setError(null);

        if (cachedAvatarForUserId === currentUserId) {
          if (isMounted) {
            setAvatarUrl(cachedAvatarUrl);
            setLoading(false);
          }
          return;
        }

        if (!avatarFetchPromise || avatarFetchForUserId !== currentUserId) {
          avatarFetchForUserId = currentUserId;
          avatarFetchPromise = (async () => {
            const picRes = await fetch(`${API_BASE}/api/profiles/me/picture`, {
              credentials: 'include',
              cache: 'no-store',
            });
            if (!picRes.ok) return null;
            const blob = await picRes.blob();
            return URL.createObjectURL(blob);
          })().finally(() => {
            avatarFetchPromise = null;
          });
        }

        const nextAvatarUrl = await avatarFetchPromise;
        if (avatarFetchForUserId === currentUserId) {
          updateCachedAvatar(currentUserId, nextAvatarUrl);
        }

        if (!isMounted) return;
        setAvatarUrl(nextAvatarUrl);
      } catch {
        if (!isMounted) return;
        setAvatarUrl(cachedAvatarForUserId === currentUserId ? cachedAvatarUrl : null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadAvatar();
    const timeoutId = window.setTimeout(() => setProfileBtnMounted(true), 20);
    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [user?.id]);

  useEffect(() => {
    const routeKey = (panelParam || '').trim().toLowerCase();
    const nextAction = routeToAction[routeKey];
    if (!routeKey || !nextAction) {
      navigate('/profiles/my-requests', { replace: true });
      setSelectedAction('view');
      return;
    }
    setSelectedAction(nextAction);
  }, [panelParam, navigate]);

  // Dynamic title: CAU {user_name} with fallback
  const titleName = (me?.name || user?.name || '').trim();
  useDocumentTitle(titleName ? `CAU ${titleName}` : t('app.title'));

  const openPanel = () => {
    setPanel('profile');
    setPanelMounted(true);
    // allow next frame for transition
    window.setTimeout(() => setPanelOpen(true), 20);
  };
  const closePanel = () => {
    setPanelOpen(false);
    window.setTimeout(() => {
      setPanelMounted(false);
      setPanel('none');
    }, 320);
  };

  const handleUploadPicture = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE}/api/profiles/me/picture`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    });
    if (!res.ok) throw new Error(t('profile.upload.uploadFailed'));
    avatarFetchPromise = null;
    avatarFetchForUserId = user?.id ?? null;
    const picRes = await fetch(`${API_BASE}/api/profiles/me/picture`, {
      credentials: 'include',
      cache: 'no-store',
    });
    if (picRes.ok) {
      const blob = await picRes.blob();
      const url = URL.createObjectURL(blob);
      updateCachedAvatar(user?.id ?? null, url);
      setAvatarUrl(url);
    }
  };

  const patchProfile = async (fields: Record<string, string>) => {
    const res = await fetch(`${API_BASE}/api/profiles/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(fields),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || 'Failed to update');
    await refresh();
    setMe((m) => ({ ...m!, ...fields } as any));
  };

  return (
    <div className="profiles-page">
      {/* Top-right profile button (since header is hidden on /profile) */}
      {/* Minimal profile button (icon-only, circular) */}
      <button
        type="button"
        className={`profile-trigger circle ${profileBtnMounted ? 'enter' : ''} ${panelMounted ? 'hidden' : ''}`}
        onClick={openPanel}
        aria-label={t('profile.openPanel')}
      >
        <span className="profile-trigger-icon" aria-hidden>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
            <path d="M4 22c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="currentColor" strokeWidth="1.8" fill="none" />
          </svg>
        </span>
      </button>

      {/* Employee Action Button (left side) */}
      <EAction
        onSelect={(a) => {
          setSelectedAction(a);
          navigate(`/profiles/${actionToRoute[a]}`);
        }}
      />

      <div className="profiles-panel show">

        {loading && <div className="status note">{t('profile.loading')}</div>}
        {error && <div className="status error">{error}</div>}

        {!loading && !error && me && (
          <div className="profile-content">
            {/* Slide-over panel */}
            {panelMounted && (
              <div className={`side-panel-overlay ${panelOpen ? 'open' : ''}`} onClick={closePanel}>
                <div className={`side-panel ${panelOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
                  <div className="side-panel-header">
                    <div className="prefs-inline">
                      <div className="theme-inline">
                        <ThemeToggle />
                      </div>
                      <LanguageSwitch mode="user" />
                    </div>
                    <button type="button" className="close-btn" onClick={closePanel} aria-label={t('profile.close')}>×</button>
                  </div>
                  <div className="side-panel-body">
                    <div className="avatar-wrap">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={t('profile.avatarAlt')} className="avatar" />
                      ) : (
                        <div className="avatar placeholder" aria-label={t('profile.avatarAlt')}>
                          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M4 22c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="currentColor" strokeWidth="1.8" fill="none" />
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="fields-grid">
                      <div className="field-box"><div className="label">{t('profile.name')}</div><div className="value">{me.name ?? '—'}</div></div>
                      <div className="field-box"><div className="label">{t('profile.surname')}</div><div className="value">{me.surname ?? '—'}</div></div>
                      <div className="field-box"><div className="label">{t('profile.department')}</div><div className="value">{(me as any).department_name ?? '—'}</div></div>
                      <div className="field-box"><div className="label">{t('profile.level')}</div><div className="value">{(me as any).level_name ?? '—'}</div></div>
                      <div className="field-box wide"><div className="label">{t('profile.mail')}</div><div className="value">{me.mail ?? '—'}</div></div>
                      <div className="field-box wide"><div className="label">{t('profile.phone')}</div><div className="value">{me.phone ?? '—'}</div></div>
                    </div>

                    <div className="edit-row">
                      <button type="button" className="edit-btn" onClick={() => setPanel('edit-menu')}>
                        <span className="pen" aria-hidden>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.4" fill="none" />
                            <path d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" stroke="currentColor" strokeWidth="1.4" fill="none" />
                          </svg>
                        </span>
                        {t('profile.editDetails')}
                      </button>
                      <button type="button" className="logout-btn" onClick={logout}>{t('profile.logout')}</button>
                    </div>

                    {/* Nested edit panels */}
                    {panel === 'edit-menu' && (
                      <div className="edit-menu">
                        <button type="button" onClick={() => setPanel('edit-picture')}>{t('profile.editMenu.changePicture')}</button>
                        <button type="button" onClick={() => setPanel('edit-name')}>{t('profile.editMenu.changeName')}</button>
                        <button type="button" onClick={() => setPanel('edit-surname')}>{t('profile.editMenu.changeSurname')}</button>
                        <button type="button" onClick={() => setPanel('edit-phone')}>{t('profile.editMenu.changePhone')}</button>
                      </div>
                    )}

                    {panel === 'edit-picture' && (
                      <div className="edit-form">
                        <label className="file-upload">
                          <input type="file" accept="image/*" onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (f) {
                              try {
                                await handleUploadPicture(f);
                                setPanel('profile');
                              } catch (err: any) {
                                alert(err.message || t('profile.upload.uploadFailed'));
                              }
                            }
                          }} />
                          <span>{t('profile.upload.choosePicture')}</span>
                        </label>
                      </div>
                    )}

                    {panel === 'edit-name' && (
                      <EditTextForm
                        label={t('profile.editForm.newName')}
                        initialValue={me.name ?? ''}
                        sameValueMessage={t('profile.editForm.nameSame')}
                        onSubmit={async (v) => { await patchProfile({ name: v }); setPanel('profile'); }}
                        onCancel={() => setPanel('profile')}
                      />
                    )}
                    {panel === 'edit-surname' && (
                      <EditTextForm
                        label={t('profile.editForm.newSurname')}
                        initialValue={me.surname ?? ''}
                        sameValueMessage={t('profile.editForm.surnameSame')}
                        onSubmit={async (v) => { await patchProfile({ surname: v }); setPanel('profile'); }}
                        onCancel={() => setPanel('profile')}
                      />
                    )}
                    {panel === 'edit-phone' && (
                      <EditTextForm
                        label={t('profile.editForm.newPhone')}
                        initialValue={me.phone ?? ''}
                        sameValueMessage={t('profile.editForm.phoneSame')}
                        numericOnly
                        maxLength={12}
                        onSubmit={async (v) => { await patchProfile({ phone: v }); setPanel('profile'); }}
                        onCancel={() => setPanel('profile')}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Dynamic action panels rendered on-page (outside overlays) */}
            <div className="dynamic-panels">
              <Suspense fallback={<div className="status note">{t('profile.loading')}</div>}>
                {selectedAction === 'warehouse' && <WarehouseRequestPanel />}
                {selectedAction === 'procurement' && <ProcurementRequestPanel />}
                {selectedAction === 'view' && <RequestsViewPanel />}
                {selectedAction === 'view_dept' && <ViewDeptRequestPanel />}
                {selectedAction === 'view_pending' && <ViewPendingRequestsPanel />}
                {selectedAction === 'moderator_pending' && <ViewCategoryRequestsModerator />}
                {selectedAction === 'purchase_start' && <PurchaseStartPanel />}
                {selectedAction === 'warehouse_start' && <WarehouseStartPanel />}
                {selectedAction === 'warehouse_management' && <WarehouseManagementPanel />}
                {selectedAction === 'view_database' && <ViewDatabasePanel />}
                {selectedAction === 'ai_chatbot' && <AIChatbotPanel />}
              </Suspense>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EditTextForm: React.FC<{
  label: string;
  initialValue: string;
  onSubmit: (value: string) => Promise<void>;
  onCancel: () => void;
  sameValueMessage?: string;
  numericOnly?: boolean;
  maxLength?: number;
}> = ({ label, initialValue, onSubmit, onCancel, sameValueMessage, numericOnly, maxLength }) => {
  const { t } = useI18n();
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = !busy && (!numericOnly || (value.trim().length === (maxLength ?? 12)));
  return (
    <form
      className="edit-form"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy) return;
        const a = (value ?? '').trim();
        const b = (initialValue ?? '').trim();
        if (a === b) {
          setError(sameValueMessage || t('profile.editForm.valueSame'));
          return;
        }
        if (numericOnly && maxLength && a.length !== maxLength) {
          setError(t('profile.editForm.digitsExact', { count: maxLength }));
          return;
        }
        try {
          setBusy(true);
          setError(null);
          await onSubmit(value);
        } catch (err: any) {
          const msg = err?.message || t('profile.editForm.failedSave');
          setError(msg);
        } finally {
          setBusy(false);
        }
      }}
    >
      <label>
        <span>{label}</span>
        <input
          value={value}
          onChange={(e) => {
            let v = e.target.value;
            if (numericOnly) {
              v = v.replace(/\D/g, '');
              if (maxLength) v = v.slice(0, maxLength);
            } else if (maxLength) {
              v = v.slice(0, maxLength);
            }
            setValue(v);
          }}
          maxLength={maxLength}
          inputMode={numericOnly ? 'numeric' : undefined}
          pattern={numericOnly ? '\\d*' : undefined}
        />
      </label>
      {error && <div className="status error" role="alert">{error}</div>}
      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
        <button type="submit" disabled={!canSubmit}>{t('common.save')}</button>
      </div>
    </form>
  );
};

export default ProfilesPage;
